import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useStripe } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { reduxForm } from 'redux-form';

import IwMedia from '../../../iwMedia/index';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { MainContentBlockPending } from '../../pageStructure/mainContentBlockPending/MainContentBlockPending';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { TitleSectionWrapper } from '../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { Text } from '../../ui/elements/Text/Text';
import { MoneyWithDecimals } from '../../ui/moneyWithDecimals';
import moneyStyles from '../../ui/moneyWithDecimals/MoneyWithDecimals.module.css';
import { NextRepaymentSummary } from '../../ui/nextRepaymentSummary/NextRepaymentSummary';
import PairItemContainer from '../../ui/pairItemContainer/PairItemContainer';
import {
    CONFIRM_EARLY_REPAYMENT,
    SELECT_REPAYMENT_METHOD,
    SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP
} from '../EarlyRepayment.actions';
import {
    MANUAL_BANK_TRANSFER_OPTION,
    TRUELAYER_REPAYMENT_TYPE
} from '../EarlyRepaymentHelper';
import styles from './ConfirmRepayment.module.css';
import { PaymentMethodInfo } from './PaymentMethodInfo';
import { SelectPaymentMethod } from './SelectPaymentMethod';
import { TSelectedPayment } from '../EarlyRepayment.reducer';

const confirmRepaymentForm = {
    form: 'confirmRepayment',
    enableReinitialize: true
};

type TPaymentType = {
    type: string;
    label: string;
    issuer: string;
    brand: string;
    cardLastDigits: string;
    value: string;
} | null;

const mapStateToProps = (state: RootState) => ({
    ...state.earlyRepayment,
    initialValues: {
        payment: state.earlyRepayment.selectedPayment
    }
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToChooseAmountStep: () =>
        dispatch({ type: SHOW_CHOOSE_REPAYMENT_AMOUNT_STEP }),
    showSchedule: () => dispatch({ type: SHOW_REPAYMENT_SCHEDULE_MODAL }),
    selectRepaymentMethod: (selectedPayment: TSelectedPayment) =>
        dispatch({ type: SELECT_REPAYMENT_METHOD, selectedPayment }),
    confirmAction: (stripe: Stripe, paymentType: TPaymentType) =>
        dispatch({ type: CONFIRM_EARLY_REPAYMENT, stripe, paymentType })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const ConfirmRepayment = ({
    ready,
    selectedInputAmount,
    remainingOutstandingBalance,
    goToChooseAmountStep,
    confirmAction,
    nextRepaymentAmount,
    nextRepaymentDate,
    previousNextRepaymentAmount,
    paymentOptions,
    selectRepaymentMethod,
    selectedPayment,
    disabledRepaymentMethodSelection,
    monthlyRepaymentsCounter,
    showSchedule
}: PropsFromRedux) => {
    const stripe = useStripe();
    if (!ready) {
        return <MainContentBlockPending />;
    }

    const shouldShowExtraInfo =
        selectedPayment?.type === TRUELAYER_REPAYMENT_TYPE;

    return (
        <div className={styles.ConfirmRepayment}>
            <IwMedia from="medium">
                <TitleSectionWrapper>
                    <div className={styles.title}>
                        <FormattedMessage
                            id="_.early_repayment.confirm_repayment.title"
                            defaultMessage="Confirm repayment"
                        />
                    </div>
                </TitleSectionWrapper>
            </IwMedia>
            <MainSectionWrapper>
                <PairItemContainer>
                    {/* Left side */}
                    <div
                        className={styles.summaryContainer}
                        data-testid="repayment-summary"
                    >
                        <div className={styles.selectedAmount}>
                            <div className={styles.summaryTitle}>
                                <FormattedMessage
                                    id="_.early_repayment.confirm_repayment.summary_title"
                                    defaultMessage="You're repaying"
                                />
                            </div>

                            <div>
                                <MoneyWithDecimals
                                    className={moneyStyles.MoneyWithDecimals}
                                    value={selectedInputAmount!}
                                    integerWithSymbolClassName={
                                        styles.repayMoneyIntegers
                                    }
                                    decimalWithSeparatorClassName={
                                        styles.repayMoneyDecimals
                                    }
                                />
                            </div>
                        </div>

                        <div>
                            <FormattedMessage
                                id="_.early_repayment.confirm_repayment.summary_breakdown"
                                defaultMessage="This will reduce your outstanding balance to {newBalance}."
                                values={{
                                    newBalance: (
                                        <FormattedCurrency
                                            value={remainingOutstandingBalance!}
                                        />
                                    )
                                }}
                            />
                        </div>
                    </div>

                    {/* Right side */}
                    <div className={styles.repaymentSummary}>
                        <NextRepaymentSummary
                            date={nextRepaymentDate}
                            amount={nextRepaymentAmount}
                            previousNextRepayment={previousNextRepaymentAmount}
                        />

                        {monthlyRepaymentsCounter ? (
                            <div className={styles.showSchedule}>
                                <span
                                    className={styles.showScheduleLink}
                                    onClick={showSchedule}
                                >
                                    <FormattedMessage
                                        id="_.early_repayment.confirm_repayment.show_schedule"
                                        defaultMessage="See your new schedule in full"
                                    />
                                </span>
                            </div>
                        ) : null}
                    </div>
                </PairItemContainer>

                <div
                    className={classNames(
                        styles.choosePaymentContainer,
                        styles.choosePayment,
                        {
                            [styles.wFull]: shouldShowExtraInfo
                        }
                    )}
                >
                    {/* Left side */}
                    <div
                        className={classNames(
                            shouldShowExtraInfo
                                ? styles.choosePaymentLeftContainerWithExtraInfo
                                : styles.choosePaymentLeftContainer
                        )}
                    >
                        <div className={styles.choosePaymentTitle}>
                            {/* NB: The messages are different in UK and DE
                            because there is only option in DE */}
                            <FormattedMessage
                                id="_.early_repayment.confirm_repayment.paid_by"
                                defaultMessage="Select a preferred payment method"
                            />
                        </div>
                        <form autoComplete="off">
                            <SelectPaymentMethod
                                name="payment"
                                options={paymentOptions}
                                onChange={selectRepaymentMethod}
                                disabled={disabledRepaymentMethodSelection}
                            />
                            {selectedPayment?.label ===
                                'Instant Bank Transfer' && (
                                <p className={styles.tideNote}>
                                    <FormattedMessage
                                        id="_.early_repayment.confirm_repayment.tide_note"
                                        defaultMessage="Note: Tide bank accounts are not compatible with Instant Bank Transfer - please select a card from the dropdown instead."
                                    />
                                </p>
                            )}
                        </form>
                    </div>
                    {/* Right side */}
                    <div
                        className={classNames(
                            styles.choosePaymentRightContainer,
                            {
                                [styles.hidden]: !shouldShowExtraInfo
                            }
                        )}
                    >
                        <PaymentMethodInfo
                            selectedPaymentMethod={selectedPayment?.type!}
                        />
                    </div>
                </div>

                <div className="mb-[80px] mt-s box-border w-full pl-[32px] text-s min-m:mb-s min-l:text-m">
                    <FormattedMessage
                        id="_.early_repayment.confirm_repayment.show_manual_repayment_details_question"
                        defaultMessage="Want to manually repay? {link}."
                        values={{
                            link: (
                                <button
                                    className={styles.manualRepaymentLink}
                                    onClick={() => {
                                        confirmAction(
                                            stripe!,
                                            MANUAL_BANK_TRANSFER_OPTION
                                        );
                                    }}
                                >
                                    <FormattedMessage
                                        id="_.early_repayment.confirm_repayment.show_manual_repayment_details_link"
                                        defaultMessage="Click here to see our bank details"
                                    />
                                </button>
                            )
                        }}
                    />
                </div>

                <div className="fixed inset-x-0 bottom-0 z-10 bg-secondary-95">
                    <div className="mx-auto box-border flex w-full max-w-[60rem] flex-col px-[32px] py-l min-m:mt-0 min-m:flex-row min-m:justify-between min-m:p-xl">
                        <Button
                            onClick={goToChooseAmountStep}
                            variant="secondary"
                            size="large"
                            className="min-m:mr-xl"
                        >
                            <FormattedMessage
                                id="_.early_repayment.confirm_repayment.back"
                                defaultMessage="← Previous step"
                            />
                        </Button>

                        <div className="mt-xl flex flex-col-reverse min-m:mt-0 min-m:flex-row min-m:items-center">
                            {shouldShowExtraInfo && (
                                <Text className="mt-s text-s font-med min-m:mr-l min-m:mt-0 min-m:max-w-[300px]">
                                    You will be redirected to our payment
                                    partner, Truelayer, to complete the payment
                                </Text>
                            )}
                            <Button
                                onClick={() => {
                                    confirmAction(stripe!, null);
                                }}
                                size="large"
                            >
                                <FormattedMessage
                                    id="_.early_repayment.confirm_repayment.next"
                                    defaultMessage="Make repayment"
                                />
                            </Button>
                        </div>
                    </div>
                </div>
            </MainSectionWrapper>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<any, PropsFromRedux>(confirmRepaymentForm)(ConfirmRepayment));
